import { useState } from 'react';
import { companionFirstTalk, companionFirstTalkPractice, companionFirstTalkSave, type CompanionFirstTalk, type CompanionFirstTalkPatch } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import type { QueryState } from '../../useQuery';
import { A, firstName, st, useApi } from './ui';

// The first talk ("Getting to know you", design roSu*, L41279): four prompts, one at a
// time, resumable, on the resident's Atlas and on the Aithora Home member's Today.
// Every answer is a real call to /residents/:id/onboarding; the practice help call pages
// nobody (POST /residents/:id/onboarding/practice-help).

/** The first talk for one person, from the server. */
export function useFirstTalk(residentId: string | null): QueryState<CompanionFirstTalk> {
  return useApi<CompanionFirstTalk>((c, signal) => companionFirstTalk(c, { params: { id: residentId! }, signal }), [residentId], !!residentId);
}

const NICK: Record<string, string[]> = { Eleanor: ['Ellie'], Margaret: ['Maggie'], Dorothy: ['Dot'], Elizabeth: ['Betty'] };

const big = (primary: boolean, red?: boolean) =>
  `display:inline-flex;align-items:center;justify-content:center;min-height:64px;padding:0 28px;border-radius:14px;font-size:21px;font-weight:800;text-decoration:none;white-space:nowrap;box-sizing:border-box;${red ? 'color:#fff;background:#B3261E;' : primary ? 'color:#fff;background:#1D3F6B;' : 'color:#22272E;background:#FCFAF4;border:2px solid rgba(34,39,46,.22);'}`;

/** The first talk: four prompts, one at a time, resumable (design roSu*, L41279). */

export function FirstTalk({
  residentId,
  fullName,
  onBigger,
  q,
  home = false,
  readOnlyReason = null,
  label = 'First talk',
}: {
  residentId: string;
  fullName: string;
  onBigger: () => void;
  /** The first talk as GET /residents/:id/onboarding returns it (useFirstTalk). */
  q: QueryState<CompanionFirstTalk>;
  /** At home there is no red button or nurse: the design says Emergency and "your family". */
  home?: boolean;
  /** Set while an Operator looks at a member's Home: every answer is off. */
  readOnlyReason?: string | null;
  label?: string;
}) {
  const { client } = useSession();
  const [tried, setTried] = useState(false);
  const [heard, setHeard] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const s = q.data;
  if (!s || !s.show) return null;
  const first = firstName(fullName);
  const last = fullName.trim().split(/\s+/).slice(-1)[0] ?? '';
  const call = s.preferredName || first;
  const save = async (patch: CompanionFirstTalkPatch) => {
    setErr(null);
    try {
      const d = await companionFirstTalkSave(client, { params: { id: residentId }, body: patch });
      q.setData(() => d);
    } catch (e) {
      setErr((e as Error).message || 'That did not save. Try again.');
    }
  };
  const practice = async () => {
    setErr(null);
    try {
      await companionFirstTalkPractice(client, { params: { id: residentId } });
      setTried(true);
      // the server moved the talk on to step 4; read it so "Got it" lands there
      q.reload();
    } catch (e) {
      setErr((e as Error).message || 'That did not work. Try again.');
    }
  };
  const msg = s.firstUnreadFamilyMessage ?? null;
  // The server counts steps 1 to 4; the practice call moves it to 4, so "Got it" is held here.
  const step = tried ? 2 : Math.min(Math.max(s.step - 1, 0), 3);
  const finish = () => void save({ step: 4, completed: true });
  let say = '';
  let sub = '';
  let btns: { label: string; onClick: () => void; style: string }[] = [];
  if (step === 0) {
    say = 'Hello, I’m Atlas. Can you hear me?';
    sub = 'If the words are too small, I can make them bigger.';
    btns = [
      { label: 'Yes, I can hear you', onClick: () => void save({ step: 2 }), style: big(true) },
      {
        label: 'Make the words bigger',
        onClick: () => {
          onBigger();
          void save({ textSize: 'largest' });
        },
        style: big(false),
      },
    ];
  } else if (step === 1) {
    say = 'What would you like me to call you?';
    btns = [first, `Mrs. ${last}`, ...(NICK[first] ?? [])].map((n, i) => ({ label: n, onClick: () => void save({ step: 3, preferredName: n }), style: big(i === 0) }));
  } else if (step === 2) {
    say = `Nice to meet you, ${call}. If you ever need help, ${home ? 'tap Emergency at the top.' : 'press the red button.'}`;
    sub = tried
      ? `That’s exactly how it works. This was practice: ${home ? 'your family' : 'your nurse'} was NOT called.`
      : `Or say ${home ? '“Hey Siri, call my family”' : '“Hey Siri, call my nurse”'} from anywhere in the room. Let’s try it once. This is practice, nobody will be called.`;
    btns = tried
      ? [{ label: 'Got it', onClick: () => setTried(false), style: big(true) }]
      : [{ label: home ? 'Call my family (practice)' : 'Call a nurse (practice)', onClick: () => void practice(), style: big(false, true) }];
  } else if (msg) {
    say = `${firstName(msg.fromName)} sent you a message. Would you like to hear it?`;
    sub = heard ? `“${msg.body}”, ${firstName(msg.fromName)}` : '';
    btns = heard
      ? [{ label: 'That’s lovely. Thank you', onClick: finish, style: big(true) }]
      : [
          {
            label: 'Yes, read it to me',
            onClick: () => {
              setHeard(true);
              void save({ playedMessageId: msg.id });
            },
            style: big(true),
          },
          { label: 'Later', onClick: finish, style: big(false) },
        ];
  } else {
    say = `That is everything for now, ${call}.`;
    btns = [{ label: 'Thank you', onClick: finish, style: big(true) }];
  }
  return (
    <div data-screen-label={label} role="region" aria-label="Getting to know you" style={st('display:flex;flex-direction:column;gap:14px;padding:20px 22px;border-radius:16px;background:rgba(79,138,214,.10);border:2px solid rgba(47,92,151,.28);box-sizing:border-box;min-width:0;')}>
      <div style={st('display:flex;align-items:center;gap:10px;flex-wrap:wrap;min-width:0;')}>
        <span style={st('font-size:15px;font-weight:700;color:#2F5C97;')}>{`Getting to know you · ${step + 1} of 4`}</span>
        <span style={st('flex:1 1 60px;display:flex;gap:6px;max-width:180px;')}>
          {[0, 1, 2, 3].map((i) => (
            <span key={i} style={st(`flex:1 1 0;height:6px;border-radius:3px;background:${i <= step ? '#2F5C97' : 'rgba(34,39,46,.14)'};`)} />
          ))}
        </span>
      </div>
      <div aria-live="polite" style={st('font-size:26px;font-weight:800;line-height:1.3;color:#22272E;text-wrap:pretty;')}>{say}</div>
      {sub && <div style={st('font-size:20px;line-height:1.5;color:#22272E;text-wrap:pretty;')}>{sub}</div>}
      <div style={st('display:flex;gap:12px;flex-wrap:wrap;min-width:0;')}>
        {btns.map((b) => (
          <A key={b.label} write disabledReason={readOnlyReason} onClick={b.onClick} s={b.style}>
            {b.label}
          </A>
        ))}
      </div>
      {err && <div role="alert" style={st('font-size:17px;color:#8A1C16;')}>{err}</div>}
      <div style={st('font-size:17px;color:#3C454F;line-height:1.45;text-wrap:pretty;')}>You can also just answer out loud. If you stop, I’ll pick up here next time.</div>
    </div>
  );
}
